import React, { useState } from "react";
import {
  updateCompliance,
  exportData as exportDataRequest,
  deleteAccount as deleteAccountRequest,
} from "@/services/auth.service";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Section, Hint } from "./SettingsShared";

const ComplianceTab = ({ user, refreshUser }) => {
  const [form, setForm] = useState({
    legal_name: user.legal_name || "",
    tax_country: user.tax_country || "",
    tax_classification: user.tax_classification || "individual",
  });
  const [delConfirm, setDelConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    try { await updateCompliance(form); toast.success("Compliance saved"); await refreshUser(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const exportData = async () => {
    setBusy(true);
    try {
      const data = await exportDataRequest();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `payd-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const deleteAccount = async () => {
    setBusy(true);
    try {
      await deleteAccountRequest();
      toast.success("Account deleted");
      window.location.href = "/login";
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); setBusy(false); }
  };

  return (
    <>
      <Section title="Tax & compliance" description="We use this to generate 1099s and cross-border receipts when you start collecting real money." data-testid="compliance-section">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Legal name</Label>
            <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="mt-1.5 h-11" data-testid="legal-name-input" />
          </div>
          <div>
            <Label>Tax country</Label>
            <Input value={form.tax_country} onChange={(e) => setForm({ ...form, tax_country: e.target.value })} placeholder="US" className="mt-1.5 h-11" data-testid="tax-country-input" />
          </div>
          <div>
            <Label>Tax classification</Label>
            <select value={form.tax_classification} onChange={(e) => setForm({ ...form, tax_classification: e.target.value })} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="tax-classification-select">
              <option value="individual">Individual</option>
              <option value="business">Business</option>
            </select>
          </div>
          <div>
            <Label>Tax form upload</Label>
            <div className="mt-1.5 h-11 flex items-center gap-2 px-3 border border-dashed border-border rounded-md text-xs text-muted-foreground" data-testid="tax-form-placeholder">
              W-9 / W-8 upload · Coming soon
            </div>
          </div>
        </div>
        <div className="flex justify-end"><Button onClick={save} data-testid="compliance-save-btn">Save</Button></div>
      </Section>
      <Section title="Data & account" description="Export everything we have on you, or permanently close your account." data-testid="data-account-section">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportData} disabled={busy} data-testid="export-data-btn">Export my data (JSON)</Button>
          <Button variant="outline" onClick={() => setDelConfirm(true)} disabled={busy} className="text-red-600 hover:text-red-700 hover:border-red-300" data-testid="delete-account-btn">Delete account</Button>
        </div>
        <Hint>Deleting your account anonymizes your profile and revokes all sessions. Contracts stay referenced under “Deleted user” so counterparties keep their records.</Hint>
      </Section>
      <Dialog open={delConfirm} onOpenChange={setDelConfirm}>
        <DialogContent data-testid="delete-account-modal">
          <DialogHeader><DialogTitle>Delete your PAYD account?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>This can't be undone. Before deleting we'll block you if:</p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-1">
              <li>You have funds still in escrow on any contract</li>
              <li>You have any open / under-review disputes</li>
            </ul>
            <p className="text-muted-foreground">Consider exporting your data first.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelConfirm(false)}>Cancel</Button>
            <Button onClick={deleteAccount} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-account-btn">
              {busy ? "Deleting…" : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ComplianceTab;
